import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Collapse,
  Stack,
  Checkbox,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HealthFileCard from './HealthFileCard';
import { fileHeadline, formatFileDateTimeParts } from './healthUtils';
import { attachmentsForDownload } from './healthDownloadUtils';

export default function HealthTimelineView({
  files,
  fieldOptions,
  request,
  onFileUpdate,
  onFileDelete,
  onPreview,
  downloadMode,
  downloadSelected,
  onToggleDownloadSelect,
}) {
  const [expandedId, setExpandedId] = useState(null);

  if (files.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No files match your filters.</Typography>
      </Paper>
    );
  }

  const rows = [];
  for (let i = 0; i < files.length; i += 1) {
    const file = files[i];
    const { date, time } = formatFileDateTimeParts(file.date);
    const label = fileHeadline(file) || 'Untitled';
    const expanded = expandedId === file.id;
    const isLast = i === files.length - 1;
    const canDownload = attachmentsForDownload(file).length > 0;
    const isSelected = !!downloadSelected[file.id];

    rows.push(
      <Box
        key={file.id}
        sx={{
          position: 'relative',
          pl: 3,
          pb: isLast ? 0 : 2,
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 5,
            top: 10,
            bottom: isLast ? 'auto' : 0,
            width: 2,
            bgcolor: 'divider',
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 8,
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: expanded || isSelected ? 'primary.main' : 'background.paper',
            border: 2,
            borderColor: expanded || isSelected ? 'primary.main' : 'divider',
            boxSizing: 'border-box',
          }}
        />
        <Box
          onClick={() => {
            if (downloadMode && canDownload && onToggleDownloadSelect) {
              onToggleDownloadSelect(file.id);
              return;
            }
            setExpandedId(expanded ? null : file.id);
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (downloadMode && canDownload && onToggleDownloadSelect) {
                onToggleDownloadSelect(file.id);
                return;
              }
              setExpandedId(expanded ? null : file.id);
            }
          }}
          sx={{
            cursor: 'pointer',
            py: 1,
            px: 1,
            borderRadius: 1,
            border: downloadMode && isSelected ? 2 : 0,
            borderColor: 'primary.main',
            opacity: downloadMode && !canDownload ? 0.45 : 1,
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
            {downloadMode && canDownload && (
              <Checkbox
                checked={isSelected}
                onChange={() => onToggleDownloadSelect && onToggleDownloadSelect(file.id)}
                onClick={(e) => e.stopPropagation()}
                sx={{ p: 0.5 }}
              />
            )}
            <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
              {date}
              {time ? `, ${time}` : ''}
            </Typography>
            <Typography variant="body1" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
              {label}
            </Typography>
            {!downloadMode && (
              <ExpandMoreIcon
                fontSize="small"
                sx={{
                  flexShrink: 0,
                  color: 'text.secondary',
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              />
            )}
          </Stack>
        </Box>
        <Collapse in={expanded && !downloadMode}>
          <Box sx={{ pt: 1, pb: 0.5 }}>
            <HealthFileCard
              file={file}
              fieldOptions={fieldOptions}
              request={request}
              onUpdate={onFileUpdate}
              onDelete={onFileDelete}
              onPreview={onPreview}
            />
          </Box>
        </Collapse>
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      {rows}
    </Paper>
  );
}
